/*
 * Heron Road hero transition.
 *
 * One full bleed quad and one fragment shader, drawn with raw WebGL. The
 * reference implementation reached for three.js and GSAP to do this; three.js
 * is roughly 600KB to draw a single quad, and GSAP was tweening one float, so
 * neither is here. The shader itself is the reference's "glass" effect,
 * unchanged: a circle grows from the centre, and inside it the arriving image
 * is refracted, split slightly into colour, and rimmed with light.
 *
 * Two things the reference got away with and we cannot. It sized everything
 * from the browser window, where our hero is a band of a page, so every size
 * here comes from the element. And it rendered at sixty frames a second
 * forever, where the image is completely still between transitions, so the
 * loop only runs while something is actually moving.
 *
 * If any of this is unavailable, unwanted, or broken, the markup underneath is
 * a plain <img> and the hero simply becomes a photograph.
 */
(() => {
  'use strict';

  const VERT = `
    attribute vec2 aPos;
    varying vec2 vUv;
    void main() {
      vUv = aPos * 0.5 + 0.5;
      gl_Position = vec4(aPos, 0.0, 1.0);
    }
  `;

  const FRAG = `
    precision highp float;
    uniform sampler2D uTexture1, uTexture2;
    uniform float uProgress;
    uniform vec2 uResolution, uTexture1Size, uTexture2Size, uFocal1, uFocal2;
    uniform float uGlobalIntensity, uSpeedMultiplier, uDistortionStrength;
    uniform float uRefraction, uChromatic, uClarity, uEdgeGlow, uLiquidFlow;
    varying vec2 vUv;

    /* Cover fit, with the crop anchored on a focal point rather than always on
       the centre. Two of the photographs are portrait in a landscape hero, and
       a centre crop would take the faces off the top. */
    vec2 coverUV(vec2 uv, vec2 texSize, vec2 focal) {
      vec2 s = uResolution / texSize;
      float scale = max(s.x, s.y);
      vec2 scaled = texSize * scale;
      vec2 offset = (uResolution - scaled) * focal;
      return (uv * uResolution - offset) / scaled;
    }

    void main() {
      float progress = uProgress;
      float time = progress * 5.0 * uSpeedMultiplier;
      vec2 uv1 = coverUV(vUv, uTexture1Size, uFocal1);
      vec2 uv2 = coverUV(vUv, uTexture2Size, uFocal2);

      float maxR = length(uResolution) * 0.85;
      float br = progress * maxR;
      vec2 p = vUv * uResolution;
      vec2 c = uResolution * 0.5;
      float d = length(p - c);
      float nd = d / max(br, 0.001);
      float param = smoothstep(br + 3.0, br - 3.0, d);

      vec4 img;
      if (param > 0.0) {
        float ro = 0.08 * uRefraction * uDistortionStrength * uGlobalIntensity
                 * pow(smoothstep(0.3 * uClarity, 1.0, nd), 1.5);
        vec2 dir = (d > 0.0) ? (p - c) / d : vec2(0.0);
        vec2 distUV = uv2 - dir * ro;
        distUV += vec2(sin(time + nd * 10.0), cos(time * 0.8 + nd * 8.0))
                * 0.015 * uLiquidFlow * uSpeedMultiplier * nd * param;
        float ca = 0.02 * uChromatic * uGlobalIntensity * pow(smoothstep(0.3, 1.0, nd), 1.2);
        img = vec4(
          texture2D(uTexture2, distUV + dir * ca * 1.2).r,
          texture2D(uTexture2, distUV + dir * ca * 0.2).g,
          texture2D(uTexture2, distUV - dir * ca * 0.8).b,
          1.0
        );
        float rim = smoothstep(0.95, 1.0, nd) * (1.0 - smoothstep(1.0, 1.01, nd));
        img.rgb += rim * 0.08 * uEdgeGlow * uGlobalIntensity;
      } else {
        img = texture2D(uTexture2, uv2);
      }

      vec4 oldImg = texture2D(uTexture1, uv1);
      if (progress > 0.95) img = mix(img, texture2D(uTexture2, uv2), (progress - 0.95) / 0.05);
      gl_FragColor = mix(oldImg, img, param);
    }
  `;

  /* GSAP's power2.inOut, which is what the reference eased the circle with. */
  const easeInOut = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);

  const compile = (gl, type, src) => {
    const sh = gl.createShader(type);
    gl.shaderSource(sh, src);
    gl.compileShader(sh);
    if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
      console.warn('[hr-hero] shader failed:', gl.getShaderInfoLog(sh));
      gl.deleteShader(sh);
      return null;
    }
    return sh;
  };

  function startHero(root) {
    if (root.dataset.hrHeroReady === 'true') return;

    const canvas = root.querySelector('[data-hr-hero-canvas]');
    const dots = [...root.querySelectorAll('[data-hr-hero-dot]')];
    /* A handset holding four full width photographs as textures is a real
       cost, so below the tablet breakpoint it gets the smaller pair. */
    const small = window.matchMedia('(max-width: 749px)').matches;
    const slides = [...root.querySelectorAll('[data-hr-hero-slide]')].map((el) => ({
      src: (small && el.dataset.srcSmall) || el.dataset.src,
      focal: [parseFloat(el.dataset.focalX) || 0.5, parseFloat(el.dataset.focalY) || 0.5],
    }));
    if (!canvas || slides.length < 2) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return;

    const gl = canvas.getContext('webgl', {
      alpha: false, antialias: false, depth: false, stencil: false,
      powerPreference: 'low-power', preserveDrawingBuffer: false,
    });
    if (!gl) return;

    const vs = compile(gl, gl.VERTEX_SHADER, VERT);
    const fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
    if (!vs || !fs) return;
    const prog = gl.createProgram();
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      console.warn('[hr-hero] link failed:', gl.getProgramInfoLog(prog));
      return;
    }
    gl.useProgram(prog);

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const aPos = gl.getAttribLocation(prog, 'aPos');
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

    const U = {};
    [
      'uTexture1', 'uTexture2', 'uProgress', 'uResolution', 'uTexture1Size', 'uTexture2Size',
      'uFocal1', 'uFocal2', 'uGlobalIntensity', 'uSpeedMultiplier', 'uDistortionStrength',
      'uRefraction', 'uChromatic', 'uClarity', 'uEdgeGlow', 'uLiquidFlow',
    ].forEach((n) => { U[n] = gl.getUniformLocation(prog, n); });

    const num = (name, fallback) => {
      const v = parseFloat(root.dataset[name]);
      return Number.isFinite(v) ? v : fallback;
    };
    gl.uniform1f(U.uGlobalIntensity, num('intensity', 1));
    gl.uniform1f(U.uSpeedMultiplier, 1);
    gl.uniform1f(U.uDistortionStrength, num('distortion', 1));
    gl.uniform1f(U.uRefraction, num('refraction', 1));
    gl.uniform1f(U.uChromatic, num('chromatic', 1));
    gl.uniform1f(U.uClarity, 1);
    gl.uniform1f(U.uEdgeGlow, num('glow', 1));
    gl.uniform1f(U.uLiquidFlow, num('flow', 1));
    gl.uniform1i(U.uTexture1, 0);
    gl.uniform1i(U.uTexture2, 1);

    const textures = new Array(slides.length).fill(null);

    const makeTexture = (image) => {
      const t = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, t);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, image);
      /* The photographs are not powers of two, so mipmaps and repeat are both
         off the table. Clamp and linear are the only safe combination. */
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      return { tex: t, size: [image.naturalWidth, image.naturalHeight] };
    };

    const load = (i) => new Promise((resolve) => {
      if (textures[i]) { resolve(textures[i]); return; }
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => { textures[i] = makeTexture(img); resolve(textures[i]); };
      img.onerror = () => resolve(null);
      img.src = slides[i].src;
    });

    let current = 0;
    let progress = 0;
    let from = 0;
    let to = 0;
    let animating = false;
    let frame = null;
    let timer = null;
    let elapsed = 0;
    let lastTick = 0;
    let visible = true;
    let destroyed = false;

    const dwell = num('dwell', 5000);
    const duration = num('duration', 2500);
    /* The first turn is deliberately short. A visitor who lands and scrolls
       within a few seconds would otherwise never see the transition at all,
       which is the whole reason the hero is built this way. Long enough for
       the canvas to finish fading in over the still, and no longer. */
    const intro = num('intro', 1200);
    let introDone = false;
    /* The observers below start watching before the first textures have
       finished loading, and their opening callback would otherwise call
       schedule() first, spend the short intro turn on nothing, and then have
       its timer cancelled by the real one. Nothing schedules until boot says
       the hero is actually running. */
    let started = false;

    const sizeCanvas = () => {
      const w = Math.max(1, Math.round(root.clientWidth));
      const h = Math.max(1, Math.round(root.clientHeight));
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.uniform2f(U.uResolution, w, h);
      draw();
    };

    const bind = (unit, slot, index) => {
      const t = textures[index];
      if (!t) return false;
      gl.activeTexture(gl.TEXTURE0 + unit);
      gl.bindTexture(gl.TEXTURE_2D, t.tex);
      gl.uniform2f(U[slot + 'Size'], t.size[0], t.size[1]);
      gl.uniform2f(U[unit === 0 ? 'uFocal1' : 'uFocal2'], slides[index].focal[0], slides[index].focal[1]);
      return true;
    };

    const draw = () => {
      if (destroyed) return;
      if (!bind(0, 'uTexture1', from) || !bind(1, 'uTexture2', to)) return;
      gl.uniform1f(U.uProgress, progress);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    };

    /* The dot fill is a CSS transition rather than something redrawn each
       frame. That is what lets the render loop stop completely between
       transitions: while an image is simply sitting there, this file does no
       work at all, and the browser animates the fill on its own. */
    const armDot = (index, ms) => {
      dots.forEach((dot, i) => {
        const active = i === index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
        const bar = dot.querySelector('.hr-hero__dot-fill');
        if (!bar) return;
        bar.style.transition = 'none';
        bar.style.transform = 'scaleX(0)';
        if (active && ms) {
          /* Reading a layout value commits the reset above, so the transition
             below starts from zero instead of being collapsed into it. */
          void bar.offsetWidth;
          bar.style.transition = `transform ${ms}ms linear`;
          bar.style.transform = 'scaleX(1)';
        }
      });
    };

    const tick = (now) => {
      frame = null;
      if (destroyed || !animating) return;
      const dt = lastTick ? now - lastTick : 16;
      lastTick = now;
      elapsed += dt;

      const t = Math.min(1, elapsed / duration);
      progress = easeInOut(t);
      draw();

      if (t >= 1) {
        animating = false;
        progress = 0;
        from = to;
        current = to;
        draw();
        schedule();
        return;
      }
      frame = requestAnimationFrame(tick);
    };

    /* Land the current transition at once. Used whenever the loop is about to
       stop, so the hero is never left mid ripple with animating still true,
       which is a state nothing recovers from: the loop is gone, and every
       path back in refuses to schedule while a transition looks live. */
    const settle = () => {
      if (!animating) return;
      animating = false;
      progress = 0;
      from = to;
      current = to;
      elapsed = 0;
      draw();
    };

    const startLoop = () => {
      if (frame !== null || destroyed) return;
      lastTick = 0;
      frame = requestAnimationFrame(tick);
    };
    const stopLoop = () => {
      if (frame !== null) cancelAnimationFrame(frame);
      frame = null;
      lastTick = 0;
    };

    /* Wait out this slide's turn, then move on. A timeout rather than a frame
       loop, because nothing is moving. */
    const schedule = () => {
      clearTimeout(timer);
      if (destroyed || !visible || slides.length < 2) { armDot(current, 0); return; }
      const wait = introDone ? dwell : intro;
      introDone = true;
      armDot(current, wait);
      timer = setTimeout(() => go((current + 1) % slides.length), wait);
    };

    const go = async (index) => {
      if (destroyed || animating || index === current) return;
      clearTimeout(timer);
      const texture = await load(index);
      if (!texture || destroyed) return;
      from = current;
      to = index;
      progress = 0;
      elapsed = 0;
      animating = true;
      armDot(index, 0);
      startLoop();
    };

    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => go(i));
      dot.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); go((current + 1) % slides.length); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); go((current - 1 + slides.length) % slides.length); }
      });
    });

    /* Off screen, or in a background tab, nothing needs to be drawn. */
    const io = new IntersectionObserver((entries) => {
      visible = entries[0].isIntersecting;
      if (visible) { if (started && !animating) schedule(); }
      else { settle(); stopLoop(); clearTimeout(timer); armDot(current, 0); }
    }, { threshold: 0.05 });
    io.observe(root);

    const onVisibility = () => {
      if (document.hidden) { settle(); stopLoop(); clearTimeout(timer); armDot(current, 0); }
      else if (started && visible && !animating) { schedule(); }
    };
    document.addEventListener('visibilitychange', onVisibility);

    const ro = new ResizeObserver(sizeCanvas);
    ro.observe(root);

    const onLost = (e) => { e.preventDefault(); destroyed = true; stopLoop(); root.classList.remove('is-gl'); };
    canvas.addEventListener('webglcontextlost', onLost);

    const destroy = () => {
      destroyed = true;
      stopLoop();
      clearTimeout(timer);
      io.disconnect();
      ro.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      canvas.removeEventListener('webglcontextlost', onLost);
      textures.forEach((t) => t && gl.deleteTexture(t.tex));
      /* Contexts are a capped resource, about sixteen per page, and the theme
         editor reloads a section on every setting change. Without this, editing
         the hero a dozen times exhausts them and the canvas goes black. */
      const ext = gl.getExtension('WEBGL_lose_context');
      if (ext) ext.loseContext();
      root.dataset.hrHeroReady = 'false';
    };
    root.hrHeroDestroy = destroy;

    /* First two frames are enough to start; the rest arrive in the background. */
    (async () => {
      const first = await load(0);
      if (!first || destroyed) return;
      from = 0; to = 0; progress = 0;
      sizeCanvas();
      root.classList.add('is-gl');
      root.dataset.hrHeroReady = 'true';

      /* Only the next image has to be ready before the first transition can
         run. Waiting on all four would put the opening ripple behind the
         slowest of them, which on a poor connection is the difference between
         seeing it and not. */
      await load(1 % slides.length);
      if (destroyed) return;
      started = true;
      schedule();
      for (let i = 2; i < slides.length; i++) await load(i);
    })();
  }

  const boot = () => document.querySelectorAll('[data-hr-hero]').forEach(startHero);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:unload', (e) => {
    const root = e.target.querySelector('[data-hr-hero]');
    if (root && root.hrHeroDestroy) root.hrHeroDestroy();
  });
  document.addEventListener('shopify:section:load', (e) => {
    const root = e.target.querySelector('[data-hr-hero]');
    if (root) startHero(root);
  });
})();
